import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, CircleUser } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export const Header: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: 'Inicio' },
    { to: '/matches', label: 'Partidos', protected: true },
    { to: '/predictions', label: 'Predicciones', protected: true },
    { to: '/my-bets', label: 'Mis Apuestas', protected: true },
    { to: '/pricing', label: 'Planes', highlight: true },
  ];

  const isActive = (path: string) => location.pathname === path;

  const userInitial = user?.user_metadata?.full_name?.[0]?.toUpperCase()
    || user?.email?.[0]?.toUpperCase()
    || 'U';

  return (
    <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-[100] backdrop-blur-lg bg-slate-900/95">
      <nav className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-3 md:py-7">

        {/* ── Desktop ─────────────────────────────────────────── */}
        <div className="hidden md:flex items-center justify-between">
          <Link to="/" className="flex items-center group">
            <img src="/logo.png" alt="BetAI" className="h-11 w-auto object-contain scale-[3.0] group-hover:scale-[3.1] transition-transform" />
          </Link>

          <div className="flex items-center gap-4">
            {navLinks.map((link) => {
              if (link.protected && !isAuthenticated) return null;
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={link.highlight
                    ? 'px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-500/20'
                    : active
                    ? 'px-4 py-2 bg-blue-500/20 text-blue-400 font-semibold rounded-lg border border-blue-500/30'
                    : 'px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-800 font-semibold rounded-lg transition-all'}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="flex items-center gap-2 text-slate-300 hover:text-white font-semibold transition-colors">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <CircleUser className="w-4 h-4 text-white" />
                  </div>
                  <span>{user?.email?.split('@')[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 font-semibold rounded-lg transition-all"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Salir</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 text-blue-400 font-semibold hover:text-blue-300 transition-colors">
                  Iniciar Sesión
                </Link>
                <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-lg transition-all shadow-lg shadow-blue-500/30">
                  Registrarse
                </Link>
              </>
            )}
          </div>
        </div>

        {/* ── Mobile ──────────────────────────────────────────── */}
        <div className="flex md:hidden items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img src="/logo.png" alt="BetAI" className="h-9 w-auto object-contain scale-[3.0] transition-transform" />
          </Link>

          {/* Right side: profile avatar or login button */}
          {isAuthenticated ? (
            <Link
              to="/profile"
              className="flex items-center gap-2 min-h-[44px] min-w-[44px] justify-end"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-blue-500/30">
                {userInitial}
              </div>
            </Link>
          ) : (
            <Link
              to="/login"
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-lg text-sm min-h-[44px] flex items-center"
            >
              Entrar
            </Link>
          )}
        </div>

      </nav>
    </header>
  );
};
